// session types

// sync with Testlet.status
export enum TestletStatus {
  NOT_STARTED = 0,
  UNLOCKED = 1,
  LOCKED = 2,
  COMPLETED = 3,
}

export const TestletStatusLabels = {
  [TestletStatus.NOT_STARTED]: 'Not Started',
  [TestletStatus.LOCKED]: 'Locked',
  [TestletStatus.UNLOCKED]: 'Unlocked',
  [TestletStatus.COMPLETED]: 'Completed',
};

// sync with TestletSession.status
export enum StudentTestletSessionStatus {
  NOT_STARTED = 0,
  IN_PROGRESS = 1,
  PAUSED = 2,
  COMPLETED = 3,
}

export const StudentTestletSessionStatusLabels = {
  [StudentTestletSessionStatus.NOT_STARTED]: 'Not Started',
  [StudentTestletSessionStatus.IN_PROGRESS]: 'In Progress',
  [StudentTestletSessionStatus.PAUSED]: 'Paused',
  [StudentTestletSessionStatus.COMPLETED]: 'Completed',
};

export interface Testlet {
  id: number;
  activity_template: string;
  name: string;
  course: number;
  duration: number;
  students_completion: number;
  status: TestletStatus;
  locked_at: string;
  unlocked_at: string;
  reset_at: string;
  exam_section: number;
  exam_session: number;
  auto_lock_duration: number; // in minutes
  is_branching: boolean;
  force_complete: boolean;
  lock_strategy: LockStrategy;
}
export interface Exam {
  id: number;
  name: string;
  duration: number;
  exam_type: number;
  originalSections: any[];
  scores: any[];
  sections: any[];
  source_url: string;
  has_pm_scoring: boolean;
}

export interface TestSession {
  id: number;
  start_date: string;
  end_date: string;
  practice_test_order: number;
  session_type?: number;
  section: number;
  exam: Exam;
}

export interface ILEExamSession extends TestSession {
  id: number;
  testlets: Testlet[];
  policy_enforced?: boolean;
  policy_start_date?: string | null;
  policy_end_date?: string | null;
  can_add_extra_sessions?: boolean;
  section_start_date?: string;
  section_end_date?: string;
}

export type MappedTestSession = {
  id: number;
  examName: string;
  examType: string;
  examDate: string;
} & ILEExamSession;

export interface TestletSessionTimeSettings {
  time_elapsed: number;
  extended_duration: number;
  updated_at: string;
}

export enum TestletSessionActivityActiveType {
  FOCUSED = 'TESTLET_FOCUSED',
  UNFOCUSED = 'TESTLET_UNFOCUSED',
}

export interface TestletSession {
  testlet: number;
  allowed_to_start?: boolean;
  id: number;
  first_name: string;
  last_name: string;
  user_id: number;
  status: StudentTestletSessionStatus;
  started_at: string;
  completed_at: string;
  session_id: string;
  activity_id: string;
  activity_template: string;
  resumed_at: string;
  time_settings: TestletSessionTimeSettings;
  original_duration: number;
  scores: {
    max_score: number;
    score: number;
    responses: {
      score: number;
      attempted: boolean;
      max_score: number;
      response_id: string;
      item_reference: string;
      dt_score_updated: Date;
      question_reference: string;
    }[];
  } | null;
  score: number;
  max_score: number;
  active?: TestletSessionActivityActiveType;
  course: number;
  exam_section: number;
}

export interface TestletSessionActivity {
  id: number;
  testlet_session_id: number;
  event_type: string;
  metadata: any;
  timestamp: string;
}

export interface TestSessionCompletionStatus {
  testlet_id: number;
  exam_session_id: number;
  is_complete: boolean;
  status?: number;
}

export type EditTestSessionData = {
  id: number;
  curriculum: number;
  end_date: string;
  length_type: number;
  published: boolean;
  section: number;
  session_type: number;
  sittings_number: number;
  start_date: string;
};

export type AddTestSessionData = {
  curriculum: number;
  end_date: string;
  length_type: number;
  published: boolean;
  section: number;
  session_type: number;
  sittings_number: number;
  start_date: string;
};

// LE Report types
export interface ReportScore {
  score: number;
  max_score: number;
  attempts: number;
}

export interface UserSkillScores {
  [skillId: string]: ReportScore;
}

export interface LEReportData {
  [userId: string]: UserSkillScores;
}

export interface UserStandardScores {
  [standardId: string]: ReportScore;
}

export interface UserItemsScores {
  [itemReference: string]: UserStandardScores;
}

export interface LEStandardReportData {
  [userId: string]: UserItemsScores;
}

// Skill Tree types
export interface ReportTreeNode {
  id: string;
  nodeName: string;
  subNodes: ReportTreeNode[];
}

export interface ReportTree {
  [key: string]: ReportTreeNode;
}

export interface StudentSections {
  [key: number]: string[];
}

// Strategy for when locking a testlet
export enum LockStrategy {
  USE_REMAINING_TIME = 0,
  PAUSE_IMMEDIATELY = 1,
  PAUSE_AND_SCORE = 2,
}